import { Dispatch, FunctionComponent, SetStateAction, useContext, useMemo } from 'react'
import styled, { ThemeContext } from 'styled-components'

import Body3 from 'design-system/Typography/Body/Body3'

import ErrorBox from 'components/ErrorBox'
import SelectInput from 'components/GlobalStyles/Inputs/SelectInput'
import { getFormBuilderSelectInputStyles } from 'components/GlobalStyles/Inputs/utils'
import Spacer from 'components/Spacer'

import { capitalizeWord } from 'utils/stringFormatting'

import { IRange } from '../types'

const FlexRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`

const InputContainer = styled.div`
  flex: 1;
`

interface IPriceOption {
  label: string
  value: number | undefined
}

const priceOptions: IPriceOption[] = [
  { label: '$100,000', value: 100000 },
  { label: '$200,000', value: 200000 },
  { label: '$300,000', value: 300000 },
  { label: '$400,000', value: 400000 },
  { label: '$500,000', value: 500000 },
  { label: '$600,000', value: 600000 },
  { label: '$700,000', value: 700000 },
  { label: '$800,000', value: 800000 },
  { label: '$900,000', value: 900000 },
  { label: '$1M', value: 1000000 },
  { label: '$1.5M', value: 1500000 },
  { label: '$2M', value: 2000000 },
  { label: '$2.5M', value: 2500000 },
]

const priceOptionsMin = [{ label: 'Any Price', value: undefined }, ...priceOptions]

const priceOptionsMax = [...priceOptions, { label: 'Any Price', value: undefined }]

const getOptionByValue = (options: IPriceOption[], value: number | undefined) =>
  options.find(option => option.value === value)

interface IProps {
  selectedPriceRange: IRange
  setSelectedPriceRange: Dispatch<SetStateAction<IRange>>
  error: string
  setError: Dispatch<SetStateAction<string>>
}

const PriceRangeInput: FunctionComponent<IProps> = ({
  selectedPriceRange,
  setSelectedPriceRange,
  error,
  setError,
}) => {
  const theme = useContext(ThemeContext)

  const handleSelection = (selection: number | undefined, type: 'min' | 'max') => {
    setError('')

    if (!selection) return setSelectedPriceRange({ ...selectedPriceRange, [type]: undefined })

    const errorOperator = type === 'min' ? 'greater than' : 'less than'
    const otherType = type === 'min' ? 'max' : 'min'
    const errorMessage = `${capitalizeWord(type)} price must be ${errorOperator} ${otherType} price`

    if (
      type === 'min'
      && selectedPriceRange.max
      && selection
      && selection >= selectedPriceRange.max
    ) {
      setError(errorMessage)
      setSelectedPriceRange({ ...selectedPriceRange, [type]: undefined })
      return
    }

    if (
      type === 'max'
      && selectedPriceRange.min
      && selection
      && selection <= selectedPriceRange.min
    ) {
      setError(errorMessage)
      setSelectedPriceRange({ ...selectedPriceRange, [type]: undefined })
      return
    }

    setSelectedPriceRange({ ...selectedPriceRange, [type]: selection })
  }

  const handleSelectMin = (selection: IPriceOption) => handleSelection(selection.value, 'min')

  const handleSelectMax = (selection: IPriceOption) => handleSelection(selection.value, 'max')

  // select input custom styling
  const stylesConfig = getFormBuilderSelectInputStyles({ theme })

  const stopPropagation = (event: React.MouseEvent) => event.stopPropagation()

  const minValue = useMemo(
    () => getOptionByValue(priceOptionsMin, selectedPriceRange.min),
    [selectedPriceRange.min],
  )
  const maxValue = useMemo(
    () => getOptionByValue(priceOptionsMax, selectedPriceRange.max),
    [selectedPriceRange.max],
  )

  return (
    <>
      <Body3>Price</Body3>
      <Spacer size={theme.spacings[8]} />
      <FlexRow>
        <InputContainer onClick={stopPropagation}>
          <SelectInput
            placeholder="No min"
            value={minValue}
            options={priceOptionsMin}
            onChange={handleSelectMin}
            stylesConfig={stylesConfig}
          />
        </InputContainer>
        <Spacer size={theme.spacings[8]} />
        -
        <Spacer size={theme.spacings[8]} />
        <InputContainer onClick={stopPropagation}>
          <SelectInput
            placeholder="No max"
            value={maxValue}
            options={priceOptionsMax}
            onChange={handleSelectMax}
            stylesConfig={stylesConfig}
          />
        </InputContainer>
      </FlexRow>
      {error && (
        <>
          <Spacer size={theme.spacings[8]} />
          <ErrorBox error={error} />
        </>
      )}
    </>
  )
}

export default PriceRangeInput
